import {
  DopaVerificationStatus,
  useKycUpdateKycInfoMutation,
  useKycVerifyCitizenCardMutation,
} from '@e-kyc/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApolloError } from '@shared/apollo';
import { useDisclosure } from '@shared/hooks';
import { THAI_TITLE_OPTIONS } from '@shared/utils';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useEkyc } from '../../../../../../providers';
import { isAlreadyCompletedError } from '../../../../utils';
import {
  type DopaModalVariant,
  IdCardConfirmationDopaModal,
  IdCardConfirmationFooter,
  IdCardConfirmationForm,
  IdCardConfirmationModal,
  IdCardConfirmationRetakeModal,
} from '../../_components';

import { type IdCardConfirmationFormType, useIdCardConfirmationFormSchema } from './schema';
import { getDateOptions, getExpiryYearOptions, getMonthOptions, getYearOptions } from './utils';

const EMPTY_OPTION = {
  value: '-',
  label: '-',
};

interface IdCardConfirmationFormLogicProps {
  defaultValues: IdCardConfirmationFormType;
}

export function IdCardConfirmationFormLogic(props: Readonly<IdCardConfirmationFormLogicProps>) {
  const { defaultValues } = props;
  const { setStep, userId, refetchEkycStatus } = useEkyc();

  const formContext = useForm<IdCardConfirmationFormType>({
    defaultValues: {
      ...defaultValues,
    },

    resolver: zodResolver(useIdCardConfirmationFormSchema()),
  });
  const [updateKycInfo, { loading: isUpdatingKycInfo }] = useKycUpdateKycInfoMutation();
  const [verifyCitizenCard, { loading: isVerifyingCitizenCard }] =
    useKycVerifyCitizenCardMutation();
  const watchedValues = formContext.watch();

  const [isRetakeModalOpen, { open: openRetakeModal, close: closeRetakeModal }] =
    useDisclosure(false);
  const [isConfirmModalOpen, { open: openConfirmModal, close: closeConfirmModal }] =
    useDisclosure(false);
  const [isDopaModalOpen, { open: openDopaModal, close: closeDopaModal }] = useDisclosure(false);
  const [dopaModalVariant, setDopaModalVariant] = useState<DopaModalVariant>('success');

  // Held from the moment an ALREADY_COMPLETED error arrives until the provider's status gate
  // swaps the page, so the confirm modal stays busy instead of dropping back to a live form.
  const [isAwaitingCompletedSwap, setIsAwaitingCompletedSwap] = useState(false);

  const handleConfirmEKycInfo = formContext.handleSubmit(
    async (data: IdCardConfirmationFormType) => {
      try {
        await updateKycInfo({
          variables: {
            updateKycInfoInput: {
              userId,
              dateOfBirth: new Date(
                Date.UTC(
                  parseInt(data.dateOfBirth.year),
                  parseInt(data.dateOfBirth.month),
                  parseInt(data.dateOfBirth.date),
                ),
              ),
              firstname: data.firstName,
              middleName: data.middleName || undefined,
              lastname: data.lastName,
              title: data.title,
              idCode: data.laserNumber.replace(/[^a-zA-Z0-9]/g, ''),
              expiryDate:
                data.cardExpirationDate.year === '-'
                  ? undefined
                  : new Date(
                      Date.UTC(
                        parseInt(data.cardExpirationDate.year),
                        data.cardExpirationDate.month === '-'
                          ? 0
                          : parseInt(data.cardExpirationDate.month),
                        data.cardExpirationDate.date === '-'
                          ? 1
                          : parseInt(data.cardExpirationDate.date),
                      ),
                    ),
            },
          },
        });

        const verifyResult = await verifyCitizenCard({
          variables: {
            verifyCitizenCardInput: {
              userId,
            },
          },
        });

        const status = verifyResult?.data?.kycVerifyCitizenCard?.dopaVerificationStatus;

        if (status === DopaVerificationStatus.Invalid) {
          setDopaModalVariant('invalid');
        } else if (status === DopaVerificationStatus.Expired) {
          setDopaModalVariant('expired');
        } else if (status === DopaVerificationStatus.Success) {
          formContext.reset();
          setDopaModalVariant('success');
        } else {
          setDopaModalVariant('unknown');
        }
        closeConfirmModal();
        openDopaModal();
      } catch (error) {
        console.error('Failed to confirm KYC info:', error);

        if (error instanceof ApolloError && isAlreadyCompletedError(error)) {
          // e-KYC was completed in another tab. Keep the confirm modal busy and let the
          // provider's status gate swap in the completed page - returning here also stops
          // the DOPA modal below opening on a request that never succeeded.
          setIsAwaitingCompletedSwap(true);
          refetchEkycStatus()
            .catch((refetchError) => {
              console.error('Failed to refetch e-KYC status:', refetchError);
            })
            .finally(() => {
              setIsAwaitingCompletedSwap(false);
            });
          return;
        }
        throw error; // Let the confirm modal handle the error and stay open for retry
      }
    },
    () => {
      closeConfirmModal();
    },
  );

  const handleRetake = () => {
    openRetakeModal();
  };

  const dayOptions = useMemo(() => {
    const month = watchedValues.dateOfBirth?.month;
    const year = watchedValues.dateOfBirth?.year;

    if (!month || !year) {
      return [];
    }

    const dates = getDateOptions(Number(month) + 1, Number(year));

    return dates;
  }, [watchedValues.dateOfBirth.month, watchedValues.dateOfBirth.year]);

  const expiryDateOptions = useMemo(() => {
    const month = watchedValues.cardExpirationDate?.month;
    const year = watchedValues.cardExpirationDate?.year;

    if (!month || !year || month === '-' || year === '-') {
      return [];
    }

    const dates = getDateOptions(Number(month) + 1, Number(year));

    return [EMPTY_OPTION, ...dates];
  }, [watchedValues.cardExpirationDate.month, watchedValues.cardExpirationDate.year]);

  const monthOptions = useMemo(() => {
    const months = getMonthOptions();

    return months;
  }, []);

  const expiryMonthOptions = useMemo(() => {
    const months = getMonthOptions();

    return [EMPTY_OPTION, ...months];
  }, []);

  const yearOptions = useMemo(() => {
    const years = getYearOptions();

    return years;
  }, []);

  const expiryYearOptions = useMemo(() => {
    const years = getExpiryYearOptions();

    return [EMPTY_OPTION, ...years];
  }, []);

  const customRenderMonthDisplayValue = (value: string) => {
    if (value === '-') {
      return value;
    }
    const monthNumber = parseInt(value, 10);
    return dayjs().month(monthNumber).format('MMM');
  };

  return (
    <FormProvider {...formContext}>
      <IdCardConfirmationForm
        data={{
          titleOptions: THAI_TITLE_OPTIONS,
          dayOptions,
          monthOptions,
          yearOptions,
          expiryDateOptions,
          expiryMonthOptions,
          expiryYearOptions,
          customRenderMonthDisplayValue,
        }}
      />
      <IdCardConfirmationFooter onSubmit={openConfirmModal} onRetake={handleRetake} />
      <IdCardConfirmationRetakeModal
        close={closeRetakeModal}
        isOpen={isRetakeModalOpen}
        setStep={setStep}
      />
      <IdCardConfirmationModal
        close={closeConfirmModal}
        isOpen={isConfirmModalOpen}
        isLoading={isUpdatingKycInfo || isVerifyingCitizenCard || isAwaitingCompletedSwap}
        onSubmit={handleConfirmEKycInfo}
      />
      <IdCardConfirmationDopaModal
        onClose={closeDopaModal}
        isOpen={isDopaModalOpen}
        isLoading={isUpdatingKycInfo || isVerifyingCitizenCard}
        variant={dopaModalVariant}
        setStep={setStep}
      />
    </FormProvider>
  );
}
